import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle, Lock, Clock } from 'lucide-react';
import { formatDateTimeIST } from '@/utils/dateUtils';

/**
 * Modal showing user's strike history with details.
 * Displays strike count summary, progress bar, explanation, and history list.
 *
 * Adapted from AdminUserManagement.js strike history pattern.
 *
 * @param {boolean} open - Whether modal is open
 * @param {Function} onOpenChange - Handler for open state changes
 * @param {Object} strikeData - Strike data from /attendance/my-strikes API
 */
export default function StrikeHistoryModal({ open, onOpenChange, strikeData }) {
  if (!strikeData) return null;

  const { strike_count, max_strikes, is_locked, strike_history } = strikeData;

  const getStrikeClass = (count) => {
    if (count >= 3) return 'text-red-600';
    if (count > 0) return 'text-amber-600';
    return 'text-gray-600';
  };

  const formatStrikeDate = (dateString) => {
    return formatDateTimeIST(dateString) || 'Unknown date';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Your Strike History
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Strike Summary */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <span className={`text-2xl font-bold ${getStrikeClass(strike_count)}`}>
                {strike_count}
              </span>
              <span className="text-gray-500">/{max_strikes || 3} strikes</span>
            </div>
            {is_locked && (
              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full font-medium flex items-center gap-1">
                <Lock className="h-4 w-4" />
                Account Locked
              </span>
            )}
          </div>

          {/* Strike Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                strike_count >= 3 ? 'bg-red-500' :
                strike_count > 0 ? 'bg-amber-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min((strike_count / (max_strikes || 3)) * 100, 100)}%` }}
            />
          </div>

          {/* Explanation */}
          <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
            <p className="font-medium mb-1">What are strikes?</p>
            <p>Strikes are added when a missed checkout is rejected by admin after review.
            3 strikes in a 30-day rolling window will lock your account.</p>
          </div>

          {/* Strike History List */}
          <div>
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-400" />
              History (last 30 days)
            </h4>
            {strike_history?.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {strike_history.map((strike, i) => (
                  <div key={i} className="p-3 border rounded bg-white">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {strike.date || strike.shift_date || 'Unknown date'}
                      </span>
                      <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                        Strike #{strike_history.length - i}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Rejected by {strike.rejected_by || 'Admin'}
                      {strike.rejected_at && (
                        <> on {formatStrikeDate(strike.rejected_at)}</>
                      )}
                    </div>
                    {(strike.notes || strike.rejection_reason) && (
                      <div className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                        <span className="font-medium">Reason: </span>
                        {strike.notes || strike.rejection_reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm py-4 text-center">
                No strikes in the last 30 days
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
